import { mapGetters } from "vuex";
import {
  SET_SUSIE_LOADED,
  SET_SUSIE_BANNER_VISIBLE,
  GET_SUSIE_SCRIPT_TAG,
} from "@/store/actions/common";

// Store reference to the survey-banner element once found
let surveyBannerElement = null;

// MutationObserver to watch for survey-banner element being added to DOM
let bannerObserver = null;

export const susieMixin = {
  computed: {
    ...mapGetters(["susieLoadedInSession", "susieBannerVisible"]),
  },

  methods: {
    async initializeSusie() {
      // Check if script already exists (source of truth for session tracking)
      const existingScript = document.getElementById("cai-innov-apps-config");
      if (existingScript) {
        // Update Vuex to match reality
        if (!this.susieLoadedInSession) {
          this.$store.dispatch(SET_SUSIE_LOADED, true);
        }
        this.showSusieBanner();
        return;
      }

      try {
        // Fetch complete script tag from API
        const scriptTag = await this.$store.dispatch(GET_SUSIE_SCRIPT_TAG);
        if (!scriptTag) {
          console.error("SUSIE: No script tag available");
          return;
        }

        // Always remove existing scripts if present to ensure clean state
        const existingScripts = document.querySelectorAll(
          'script[id*="cai-innov-apps"], script[src*="surveyApp"], script[src*="Innovations-apps"]'
        );
        if (existingScripts.length > 0) {
          this.removeSusieScript();
          // Wait a bit for cleanup to complete
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Parse the script tag HTML to extract attributes
        const parser = new DOMParser();
        const doc = parser.parseFromString(scriptTag, "text/html");
        const scriptElement = doc.querySelector("script");

        if (!scriptElement) {
          console.error("SUSIE: Failed to parse script tag");
          return;
        }

        // Create a new script element and copy all attributes
        const script = document.createElement("script");
        Array.from(scriptElement.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });

        // Append to body
        document.body.appendChild(script);

        // Mark as loaded for this session via Vuex
        this.$store.dispatch(SET_SUSIE_LOADED, true);

        // Start observing for the survey-banner element
        this.observeSurveyBanner();
      } catch (error) {
        console.error("SUSIE: Error initializing:", error);
      }
    },

    observeSurveyBanner() {
      // If already observing, don't create another observer
      if (bannerObserver) return;

      // Check if element already exists
      const existingBanner = document.querySelector("survey-banner");
      if (existingBanner) {
        surveyBannerElement = existingBanner;
        return;
      }

      // Create MutationObserver to watch for the survey-banner element
      bannerObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeName && node.nodeName.toLowerCase() === "survey-banner") {
              surveyBannerElement = node;
              // Stop observing once found
              bannerObserver.disconnect();
              bannerObserver = null;
              return;
            }
          }
        }
      });

      // Start observing
      bannerObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },

    removeSusieScript() {
      this.hideSusieBanner();
    },

    hideSusieBanner() {
      // Use cached reference if available, otherwise query
      const surveyBanner = surveyBannerElement || document.querySelector("survey-banner");

      if (surveyBanner) {
        surveyBanner.style.display = "none";
        surveyBannerElement = surveyBanner; // Cache reference
        this.$store.dispatch(SET_SUSIE_BANNER_VISIBLE, false);
      }
    },

    showSusieBanner() {
      // Use cached reference if available, otherwise query
      const surveyBanner = surveyBannerElement || document.querySelector("survey-banner");

      if (surveyBanner) {
        surveyBanner.style.display = "";
        surveyBannerElement = surveyBanner; // Cache reference
        this.$store.dispatch(SET_SUSIE_BANNER_VISIBLE, true);
      }
    },
  },
};
